import { useCallback, useRef, useState } from "react";
import type { ObservacaoNormalizada } from "@mostarda/core";
import { api } from "../lib/api.js";
import { importarExportSaude, type Progresso } from "../lib/apple-health-xml.js";
import { Mostarda } from "../components/Mostarda.js";

/**
 * Importação do histórico do app Saúde.
 *
 * Tela de uso raro — uma vez, talvez duas — mas é ela que faz a diferença entre
 * um app que começa do zero e um que já sabe o peso de dois anos atrás. O TDEE
 * adaptativo precisa de semanas de histórico para valer alguma coisa; sem esta
 * tela, a Laís esperaria um mês antes de o app dizer qualquer coisa útil.
 *
 * Tudo pesado acontece aqui no navegador. O que sobe são observações prontas.
 */

/** Quantos dias o servidor fecha por chamada. Ver a nota em routes/import.ts. */
const DIAS_POR_CHAMADA = 10;

type Fase = "escolher" | "importando" | "consolidando" | "pronto" | "erro";

export function ImportarSaude({ aoFechar }: { aoFechar: () => void }) {
  const [fase, setFase] = useState<Fase>("escolher");
  const [progresso, setProgresso] = useState<Progresso | null>(null);
  const [diasFeitos, setDiasFeitos] = useState({ feitos: 0, total: 0 });
  const [erro, setErro] = useState<string | null>(null);
  const [resumo, setResumo] = useState<{ enviadas: number; duplicadas: number } | null>(null);
  const abortar = useRef<AbortController | null>(null);

  const importar = useCallback(async (arquivo: File) => {
    setFase("importando");
    setErro(null);
    const ctrl = new AbortController();
    abortar.current = ctrl;

    // As datas afetadas se acumulam durante toda a leitura e só são fechadas no
    // fim. Um único lote pode tocar meses de calendário; recalcular a cada lote
    // refaria o mesmo dia dezenas de vezes.
    const datas = new Set<string>();

    try {
      const r = await importarExportSaude(arquivo, {
        sinal: ctrl.signal,
        onProgresso: setProgresso,
        enviarLote: async (itens: ObservacaoNormalizada[], timezone: string) => {
          const resp = await api.post<{ inserted: number; duplicates: number; dates: string[] }>(
            "/import/apple-health",
            { items: itens, timezone },
          );
          for (const d of resp.dates) datas.add(d);
          return { inserted: resp.inserted, duplicates: resp.duplicates };
        },
      });

      const lista = [...datas].sort();
      setDiasFeitos({ feitos: 0, total: lista.length });
      setFase("consolidando");
      for (let i = 0; i < lista.length; i += DIAS_POR_CHAMADA) {
        if (ctrl.signal.aborted) throw new DOMException("Cancelado.", "AbortError");
        await api.post("/import/consolidar", { dates: lista.slice(i, i + DIAS_POR_CHAMADA) });
        setDiasFeitos({ feitos: Math.min(i + DIAS_POR_CHAMADA, lista.length), total: lista.length });
      }

      setResumo({ enviadas: r.enviadas, duplicadas: r.duplicadas });
      setFase("pronto");
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return setFase("escolher");
      setErro(e instanceof Error ? e.message : "Deu ruim na importação.");
      setFase("erro");
    }
  }, []);

  const pct =
    progresso && progresso.bytesTotais > 0
      ? Math.min(100, Math.round((progresso.bytesLidos / progresso.bytesTotais) * 100))
      : 0;

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col gap-6 px-5 py-8">
      <header className="flex items-center gap-3">
        <Mostarda estado={fase === "pronto" ? "comemorando" : "espreitando"} className="h-16 w-16" />
        <div>
          <h1 className="text-lg font-semibold">Importar o app Saúde</h1>
          <p className="text-sm text-suave">O histórico inteiro, de uma vez.</p>
        </div>
      </header>

      {fase === "escolher" && (
        <>
          <ol className="space-y-3 text-sm text-suave">
            <li>
              <span className="font-medium text-texto">1.</span> No iPhone, abra o app{" "}
              <b>Saúde</b> → toque na sua foto (canto superior direito) →{" "}
              <b>Exportar Todos os Dados de Saúde</b>.
            </li>
            <li>
              <span className="font-medium text-texto">2.</span> A preparação demora alguns minutos.
              Ao terminar, escolha <b>Salvar em Arquivos</b>.
            </li>
            <li>
              <span className="font-medium text-texto">3.</span> Volte aqui e escolha o{" "}
              <code>exportar.zip</code> abaixo.
            </li>
          </ol>

          <label className="rounded-2xl border-2 border-dashed border-mostarda/40 px-4 py-8 text-center">
            <input
              type="file"
              accept=".zip,.xml,application/zip,text/xml"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void importar(f);
              }}
            />
            <span className="cursor-pointer text-mostarda underline">escolher o arquivo</span>
          </label>

          <p className="text-xs text-suave">
            O arquivo é lido aqui no seu celular. O XML bruto — que tem tudo que o app Saúde sabe
            sobre você — <b>não sai do aparelho</b>. Sobem só peso, sono, passos, frequência
            cardíaca e as outras métricas que o Mostarda usa.
          </p>

          <button className="text-sm text-suave underline" onClick={aoFechar}>
            agora não
          </button>
        </>
      )}

      {(fase === "importando" || fase === "consolidando") && (
        <div className="space-y-4">
          <div className="h-3 overflow-hidden rounded-full bg-mostarda/15">
            <div
              className="h-full bg-mostarda transition-[width] duration-300"
              style={{
                width:
                  fase === "consolidando"
                    ? `${diasFeitos.total ? (diasFeitos.feitos / diasFeitos.total) * 100 : 100}%`
                    : `${pct}%`,
              }}
            />
          </div>

          {fase === "importando" ? (
            <p className="text-sm text-suave" aria-live="polite">
              {pct}% — {progresso?.registrosLidos.toLocaleString("pt-BR") ?? 0} registros lidos,{" "}
              {progresso?.enviadas.toLocaleString("pt-BR") ?? 0} guardados.
            </p>
          ) : (
            <p className="text-sm text-suave" aria-live="polite">
              Fechando as contas de {diasFeitos.total.toLocaleString("pt-BR")} dias —{" "}
              {diasFeitos.feitos.toLocaleString("pt-BR")} prontos.
            </p>
          )}

          <p className="text-xs text-suave">
            Pode deixar a tela ligada e ir tomar um café. Se sair no meio, ao voltar a importação
            continua de onde parou — não recomeça.
          </p>

          <button
            className="text-sm text-suave underline"
            onClick={() => abortar.current?.abort()}
          >
            parar
          </button>
        </div>
      )}

      {fase === "pronto" && resumo && (
        <div className="space-y-4">
          <p className="text-sm">
            Entraram <b>{resumo.enviadas.toLocaleString("pt-BR")}</b> registros novos.
            {resumo.duplicadas > 0 && (
              <>
                {" "}
                Outros {resumo.duplicadas.toLocaleString("pt-BR")} já estavam aqui e foram
                ignorados — pode reimportar quantas vezes quiser, não duplica.
              </>
            )}
          </p>
          <button
            className="w-full rounded-xl bg-mostarda px-4 py-3 font-medium text-fundo"
            onClick={aoFechar}
          >
            ver o painel
          </button>
        </div>
      )}

      {fase === "erro" && (
        <div className="space-y-4">
          <p className="text-sm text-alerta">{erro}</p>
          <p className="text-xs text-suave">
            O que já subiu está salvo. Escolher o mesmo arquivo de novo continua de onde parou.
          </p>
          <button className="text-sm underline" onClick={() => setFase("escolher")}>
            tentar de novo
          </button>
        </div>
      )}
    </main>
  );
}
